import { Link } from 'react-router';
import {
  GuideLayout,
  GuideSection,
  GuideStep,
  GuideSteps,
  GuideTip,
} from '@/components/guides/guide-layout';
import { PhaseFiveGuideNotes } from '@/components/guides/phase-five-guide-notes';

const inlineLink =
  'font-medium text-brand-600 underline decoration-brand-300 underline-offset-2';

export function NationalHighwaysPotholeClaimsGuide() {
  return (
    <GuideLayout
      slug="national-highways-pothole-claims"
      category="Claim routes"
      categoryColor="brand"
      title="National Highways Pothole Claims Guide"
      seoTitle="National Highways Pothole Claims Guide | Fixtyer"
      description="How to identify an English strategic-road incident, obtain a contact reference and use National Highways’ official Red Claim route."
      readTime="7 min"
      updatedDate="2026-07-19"
      reviewedDate="2026-07-19"
      datePublished="2026-07-19"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      sources={[
        {
          name: 'National Highways: Red Claims',
          url: 'https://nationalhighways.co.uk/our-roads/network-claims/claims-against-national-highways-red-claims/',
        },
        {
          name: 'GOV.UK: claim for vehicle damage',
          url: 'https://www.gov.uk/claim-for-damage-to-your-vehicle',
        },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'For damage on an English motorway or trunk road managed by National Highways, obtain a Customer Contact Centre reference and use the official Red Claim form.',
        },
        {
          label: 'Not every A-road',
          value:
            'Some A-roads are local-authority roads. Confirm the maintaining authority before choosing a route.',
        },
        {
          label: 'No automatic payment',
          value:
            'National Highways says there is no automatic right to compensation; the facts and legal evidence are investigated.',
        },
      ]}
      relatedGuides={[
        {
          slug: 'who-is-responsible',
          title: 'Who Is Responsible for the Road?',
          readTime: '4 min',
        },
        {
          slug: 'highway-authority-claim-routes',
          title: 'Claim Routes Directory',
          readTime: '6 min',
        },
        {
          slug: 'request-road-inspection-records',
          title: 'Request Road Inspection Records',
          readTime: '8 min',
        },
      ]}
    >
      <GuideSteps>
        <GuideStep number={1} title="Confirm who maintained the road">
          Record the road number, direction, nearest junction and marker post where available. Use
          the{' '}
          <Link to="/guides/who-is-responsible" className={inlineLink}>
            road-authority guide
          </Link>{' '}
          rather than assuming every motorway-style or A-road incident belongs to National
          Highways.
        </GuideStep>
        <GuideStep number={2} title="Report the incident and obtain the required reference">
          National Highways instructs claimants to contact its Customer Contact Centre on 0300 123
          5000 first. Keep the reference: the official online Red Claim form requires it.
        </GuideStep>
        <GuideStep number={3} title="Submit through the official Red Claim form">
          Use the{' '}
          <a
            href="https://nationalhighways.co.uk/our-roads/network-claims/claims-against-national-highways-red-claims/trunk-road-and-motorway-third-party-damage-report-form-red-claims/"
            className={inlineLink}
          >
            National Highways trunk-road and motorway damage form
          </a>
          . Reporting a defect and submitting a compensation claim are separate actions.
        </GuideStep>
      </GuideSteps>

      <GuideSection title="Evidence to organise" icon={<span aria-hidden="true">✓</span>}>
        <ul className="list-disc space-y-2 pl-5">
          <li>date, time, direction, lane and precise location;</li>
          <li>the Customer Contact Centre reference and any police or recovery reference;</li>
          <li>safe photographs or dashcam footage showing location and sequence;</li>
          <li>vehicle-damage photographs, inspection findings and itemised invoices;</li>
          <li>copies of everything submitted while retaining original records.</li>
        </ul>
        <GuideTip variant="info" title="Debris is different">
          National Highways says damage caused by debris dropped by another vehicle is not a Red
          Claim against it; its guidance directs drivers to contact their insurer. Check the actual
          cause before choosing a route.
        </GuideTip>
      </GuideSection>

      <GuideSection title="If National Highways rejects the claim" icon={<span aria-hidden="true">→</span>}>
        <p>
          Ask which inspection, defect and repair records support the decision, then compare those
          records with the response. The existence of a pothole does not itself establish
          negligence, and a Section 58 response does not itself decide whether its factual basis is
          complete. Use the{' '}
          <Link to="/guides/pothole-claim-rejected-next-steps" className={inlineLink}>
            rejection next-steps guide
          </Link>{' '}
          for a careful review sequence.
        </p>
      </GuideSection>

      <PhaseFiveGuideNotes />
    </GuideLayout>
  );
}
